import { cloneDeep } from 'lodash';
import { CellData, TableContentData, TableElementChanges } from '../../../../../../common/table/TableTypes';
import { getNumericDateTimeStringFromCellValue } from '../../../../../../common/table/utils/tableInputDateTimeUtils';
import { isCellTypeDateTime } from '../../../../../../common/table/utils/tableCellDataPropertyUtils';
import { isFormula } from '../formula/tableFormulaPredicateUtils';
import { getDateFromString, getIsoStringWithoutTimezone } from '../../../../../../common/utils/timeUtil';
import { CellTypeNames } from '../../../../../../common/table/CellTypeConstants';

/**
 * Converts a persisted cell value into the editor-friendly HOT value for the current locale.
 */
export const convertReduxValueToHotValue = (cellData: CellData, locale: string): CellData => {
    const { type, value } = cellData || {};
    if (!value || !type) return cellData;

    if (type?.name === CellTypeNames.DATE_TIME && !isFormula(value)) {
        const numericDateTimeString = getNumericDateTimeStringFromCellValue(value, type, locale);
        if (numericDateTimeString) cellData.value = numericDateTimeString;
    }

    return cellData;
};

/**
 * Converts an entire persisted table snapshot into HOT-friendly cell values.
 */
export const convertDataToHotFormat = (data: TableContentData, locale: string): TableContentData =>
    data?.map((row: Array<CellData>) =>
        row?.map((cellData: CellData) => convertReduxValueToHotValue(cellData, locale)),
    );

/**
 * Converts a single HOT cell value back into the persisted table-content format.
 */
export const convertCellDataToReduxFormat = (cellData: CellData, locale: string): CellData => {
    const { value } = cellData;

    if (isCellTypeDateTime(cellData) && !isFormula(value)) {
        const date = getDateFromString(value, locale);
        const isoString = getIsoStringWithoutTimezone(date);
        if (isoString) cellData.value = isoString;
    }

    if (cellData.value === null || cellData.value === undefined || cellData.value === '') {
        cellData.value = null;
    } else {
        cellData.value = cellData.value.toString();
    }

    return cellData;
};

/**
 * Converts an entire HOT table snapshot into the persisted table-content format.
 */
export const convertDataToReduxFormat = (data: TableContentData, locale: string): TableContentData =>
    data?.map((row: Array<CellData>) =>
        row?.map((cellData: CellData) => convertCellDataToReduxFormat(cellData, locale)),
    );

/**
 * Normalises table element changes before they are persisted into Redux state.
 */
export const convertTableElementChangesToReduxFormat = (
    changes: TableElementChanges,
    locale: string,
): TableElementChanges => {
    const newChanges = cloneDeep(changes);
    if (!newChanges.tableContent?.data) return changes;

    newChanges.tableContent.data = convertDataToReduxFormat(newChanges.tableContent?.data, locale);
    return newChanges;
};

/**
 * Returns the raw cell value grid expected by HOT's `data` config.
 */
export const getCellValueArray = (data: TableContentData): Array<Array<string | null>> =>
    data.map((rows) => rows.map((cell) => cell?.value || null)) || [];
